import React, { useMemo } from "react";
import FilterCard, { FilterRemoveButton } from "./FilterCard";
import { CloudIcon } from "@heroicons/react/24/outline";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { Checkbox } from "components/Graphs/Checkbox";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import {
  GetProviderName,
  GPUInstanceType,
} from "types/InstanceTypes";
import { displayPlatformType } from "components/Environment/utils";

interface CloudFilterProps {
  selectedProviders: string[];
  setSelectedProviders: (providers: string[]) => void;
  containerSelected: ContainerSelected;
  allGPUInstances: GPUInstanceType[];
  lockedProvider?: string | null;
}

const CloudFilter: React.FC<CloudFilterProps> = ({
  selectedProviders,
  setSelectedProviders,
  containerSelected,
  allGPUInstances,
  lockedProvider,
}) => {
  // get provider list from instance types
  const providerList = useMemo(() => {
    const dgxcGCP = "GCPALPHA";
    const providers = allGPUInstances
      .map((instance) => GetProviderName(instance))
      .filter((provider): provider is string => provider !== undefined);
    return [...new Set(providers)].filter((provider) => provider !== dgxcGCP);
  }, [allGPUInstances]);

  const toggleProvider = (provider: string) => {
    setSelectedProviders(
      selectedProviders.includes(provider)
        ? selectedProviders.filter((selected) => selected !== provider)
        : [...selectedProviders, provider],
    );
  };

  if (lockedProvider) {
    return (
      <TooltipPrimitives.Provider>
        <TooltipPrimitives.Root delayDuration={0}>
          <TooltipPrimitives.Trigger asChild>
            <FilterCard
              icon={<CloudIcon className="w-4 h-4" />}
              title={displayPlatformType(lockedProvider)}
              filterApplied={true}
              buttonClassName="cursor-not-allowed opacity-60"
              aria-disabled="true"
            />
          </TooltipPrimitives.Trigger>
          <TooltipPrimitives.Portal>
            <TooltipPrimitives.Content
              side="bottom"
              className="z-50 max-w-xs rounded-md bg-zinc-800 px-3 py-2 text-xs text-zinc-200 shadow-md"
              sideOffset={4}
            >
              This launchable only works with{" "}
              {displayPlatformType(lockedProvider)}.
              <TooltipPrimitives.Arrow className="fill-zinc-800" />
            </TooltipPrimitives.Content>
          </TooltipPrimitives.Portal>
        </TooltipPrimitives.Root>
      </TooltipPrimitives.Provider>
    );
  }

  return (
    <Popover>
      <div className="relative inline-flex">
        <PopoverTrigger asChild>
          <FilterCard
            icon={<CloudIcon className="w-4 h-4" />}
            title={
              selectedProviders.length === 0
                ? "All providers"
                : selectedProviders.length === 1
                  ? displayPlatformType(selectedProviders[0])
                  : "Providers"
            }
            filterApplied={selectedProviders.length > 0}
            hasTrailingAction={selectedProviders.length > 0}
            count={
              selectedProviders.length === 0
                ? providerList.length
                : selectedProviders.length > 1
                  ? selectedProviders.length
                  : undefined
            }
          />
        </PopoverTrigger>
        {selectedProviders.length > 0 && (
          <FilterRemoveButton
            filterName="provider"
            onRemove={() => setSelectedProviders([])}
          />
        )}
      </div>
      <PopoverContent className="w-[calc(100vw-2rem)] max-w-[500px] overflow-y-auto p-4">
        <div className="mb-4 flex w-full flex-col">
          <div className="flex flex-col">
            <div className="flex flex-col w-full">
              <div className="flex flex-col space-y-2">
                {/* Specific Sources Section */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                      Provider
                    </span>
                    <div className="h-px flex-1 bg-zinc-800" />
                    <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-zinc-800/50">
                      <Checkbox
                        checked={selectedProviders.length === 0}
                        onCheckedChange={() => setSelectedProviders([])}
                      />
                      <span className="text-xs text-white">All providers</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {providerList
                      .sort((a, b) => a.localeCompare(b))
                      .map((provider) => {
                        const disabled =
                          (containerSelected?.vmBuild ||
                            containerSelected.customContainer) &&
                          provider === "akash";
                        const selected = selectedProviders.includes(provider);

                        return (
                          <label
                            key={provider}
                            className={`flex items-center gap-3 rounded-md border p-3 ${
                              selected ? "border-highlight" : "border-zinc-800"
                            } ${
                              disabled
                                ? "cursor-not-allowed opacity-50"
                                : "cursor-pointer"
                            }`}
                          >
                            <Checkbox
                              checked={selected}
                              disabled={disabled}
                              onCheckedChange={() => toggleProvider(provider)}
                            />
                            <div className="flex flex-col relative">
                              <span className="text-xs text-white uppercase">
                                {displayPlatformType(provider)}
                              </span>
                              {(containerSelected?.vmBuild ||
                                containerSelected.customContainer) &&
                                provider === "akash" && (
                                  <span className="text-[8px] text-zinc-500 leading-tight">
                                    No VMs
                                  </span>
                                )}
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CloudFilter;
